import {useEffect, useRef} from "react";
import {Column, Group, lookupNestedProp, TID} from "./Table";
import {getEditable, getEditKey} from "./TableEditCell";
import {useSyncedRef} from "../SyncedRef";
import {useSnackbar} from "../Snackbar";

// useTableCsvExport downloads the table's visible columns and filtered rows as
// {table name}.csv when the user presses Ctrl/Cmd+Shift+E while the mouse is
// over the table. Attach the returned handlers to the table's outer element.
export function useTableCsvExport<T extends TID>(props: {
    name: string;
    columns: Column<T>[];
    data: Group<T>[];
}) {
    const hover = useRef(false);
    const nameRef = useSyncedRef(props.name);
    const columnsRef = useSyncedRef(props.columns);
    const dataRef = useSyncedRef(props.data);
    const snack = useSnackbar();
    const snackRef = useSyncedRef(snack);

    useEffect(() => {
        const keydown = (e: KeyboardEvent) => {
            if(!hover.current) return;
            if(!(e.ctrlKey || e.metaKey) || !e.shiftKey) return;
            if(e.key.toLowerCase() !== "e") return;

            e.preventDefault();
            e.stopPropagation();

            const fileName = safeFileName(nameRef.current) + ".csv";
            downloadCsv(fileName, tableToCsv(dataRef.current, columnsRef.current));
            snackRef.current.success("Downloaded " + fileName);
        };

        document.addEventListener("keydown", keydown, true);
        return () => document.removeEventListener("keydown", keydown, true);
    }, [nameRef, columnsRef, dataRef, snackRef]);

    return {
        onMouseEnter: () => {
            hover.current = true;
        },
        onMouseLeave: () => {
            hover.current = false;
        },
    };
}

export function tableToCsv<T extends TID>(groups: Group<T>[], columns: Column<T>[]): string {
    const hasGroups = groups.length > 1 || (groups.length === 1 && groups[0].key !== "default");

    const header: string[] = [];
    if(hasGroups) header.push("Group");
    columns.map(c => header.push(c.name));

    const lines = [header.map(csvEscape).join(",")];

    groups.map(g => g.rows.map(row => {
        const cells: string[] = [];
        if(hasGroups) cells.push(g.key);
        columns.map(c => cells.push(cellToString(row, c)));

        lines.push(cells.map(csvEscape).join(","));
        return null;
    }));

    return lines.join("\r\n");
}

export function cellToString<T extends TID>(row: T, col: Column<T>): string {
    try {
        const rendered = reactNodeToText(col.render(row, col));
        if(rendered !== "") return rendered;

        const editable = getEditable(col, row);
        if(editable?.copy) return editable.copy(row) ?? "";

        const calc = col.calcValue?.(row) ?? editable?.calcValue?.(row);
        if(calc !== undefined && calc !== null) return formatValue(calc);

        const editKey = getEditKey(col, row);
        if(editKey) return formatValue(lookupNestedProp(row, editKey));

        return "";
    } catch (e) {
        return "";
    }
}

// reactNodeToText collects the plain-text content of a rendered cell so JSX
// cells (e.g. status chips) export their visible label
function reactNodeToText(node: any): string {
    if(node === null || node === undefined) return "";
    if(typeof node === "string") return node;
    if(typeof node === "number") return node.toString();
    if(typeof node === "boolean") return "";
    if(Array.isArray(node)) return node.map(reactNodeToText).join("");
    if(typeof node === "object" && node.props) return reactNodeToText(node.props.children);
    return "";
}

function formatValue(value: any): string {
    if(value === null || value === undefined) return "";
    if(value === true) return "Yes";
    if(value === false) return "No";
    return value.toString();
}

function csvEscape(value: string): string {
    if(/[",\n\r]/.test(value)) {
        return '"' + value.replace(/"/g, '""') + '"';
    }

    return value;
}

function safeFileName(name: string): string {
    return name.replace(/[/\\:*?"<>|]+/g, "-");
}

function downloadCsv(fileName: string, content: string) {
    const blob = new Blob([content], {type: "text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
}
